import { TokenizationTypeUserEvents } from "./modules/analytics/events/tokenization-type";
import { DistributionType } from "./types";

// // // //

export const RWA_ICON = "/rwa-logo.svg";
export const RWA_COIN_ICON = "/rwa-logo-coin.svg";

export const SOCIAL_OG_IMAGE_URL = "https://img.rwa.xyz/content/social-og.png";

export const RWA_DATA_EMAIL_ADDRESS = "data@rwa.xyz";
export const RWA_TEAM_EMAIL_ADDRESS = "team@rwa.xyz";

/**
 * Exports URL constants used throughout the app
 */
enum RwaUrls {
    blog = "blog",
    careers = "careers",
    directorySignUp = "directorySignUp",
    docs = "docs",
    docsMcp = "docsMcp",
    newswire = "newswire",
    privacyPolicy = "privacyPolicy",
    taxonomyDocs = "taxonomyDocs",
    termsOfUse = "termsOfUse",
    twitter = "twitter",
}

export const RWA_URLS: { [key in RwaUrls]: string } = {
    [RwaUrls.blog]: "https://app.rwa.xyz/blog",
    [RwaUrls.careers]: "https://jobs.ashbyhq.com/RWA.xyz",
    [RwaUrls.directorySignUp]: "https://forms.default.com/353141",
    [RwaUrls.docs]: "https://docs.rwa.xyz",
    [RwaUrls.docsMcp]: "https://docs.rwa.xyz/mcp/overview",
    [RwaUrls.newswire]: "https://rwa.news",
    [RwaUrls.privacyPolicy]: "https://www.rwa.xyz/privacy-policy",
    [RwaUrls.taxonomyDocs]:
        "https://rwa.xyz/blog/a-new-framework-for-tokenized-assets-distributed-and-represented",
    [RwaUrls.termsOfUse]: "https://www.rwa.xyz/terms-of-use",
    [RwaUrls.twitter]: "https://x.com/rwa_xyz",
};

export const ASSET_CLASS_IDS = {
    TreasuryProduct: 27,
    Stablecoin: 28,
    Commodities: 37,
    Cryptocurrency: 26,
    Stocks: 34,
    PrivateEquity: 35,
    NonUSGovernmentDebt: 30,
    MunicipalBonds: 31,
    CorporateCredit: 32,
    FiatCurrency: 25,
    RealEstate: 36,
    DiversifiedCredit: 42,
    ActiveStrategies: 52,
    AssetBackedCredit: 51,
    RepurchaseAgreements: 54,
    SpecialtyFinance: 55,
    VentureCapital: 56,
};

export const ASSET_CLASS_SLUGS = {
    ActiveStrategies: "active-strategies",
    AssetBackedCredit: "asset-backed-credit",
    Commodities: "commodities",
    CorporateCredit: "corporate-credit",
    Cryptocurrencies: "cryptocurrencies",
    DiversifiedCredit: "diversified-credit",
    FiatCurrency: "fiat-currency",
    MunicipalCredit: "municipal-credit",
    NonUsGovernmentDebt: "non-us-government-debt",
    PrivateEquity: "private-equity",
    RealEstate: "real-estate",
    RepurchaseAgreements: "repurchase-agreements",
    SpecialtyFinance: "specialty-finance",
    Stablecoins: "stablecoins",
    Stocks: "stocks",
    UsGovernmentRelatedAndAgencyDebt: "us-government-related-and-agency-debt",
    UsTreasuryDebt: "us-treasury-debt",
    VentureCapital: "venture-capital",
};

export const TRANSACTION_TYPE_IDS = {
    Transfer: 3,
};

export const MEASURE_IDS = {
    YieldToMaturity: 1,
    TotalAssetValueDollar: 2,
    AvailableCapacityDollar: 6,
    OutstandingCapitalDollar: 8,
    TotalLoansValueDollar: 10,
    FundedAssetsToken: 21,
    MarketValue: 40,
    AdjustedDailyBurnsDollar: 88,
    AdjustedDailyBurnsToken: 86,
    AdjustedDailyMintsDollar: 87,
    AdjustedDailyMintsToken: 85,
    DailyMintsToken: 16,
    DailyBurnsToken: 17,
    DailyMintsDollar: 18,
    DailyBurnsDollar: 19,
    WeeklyActiveAddresses: 42,
    DailyTransferVolumeToken: 13,
    PriceDollar: 39,
    NetAssetValueDollar: 3,
    DailyTransferVolumeDollar: 20,
    DailyTransactionsCount: 46,
    WeeklyTransferVolume: 1001,
    DailyActiveAddresses: 14,
    DailyNumberOfHolders: 15,
    HoldingAddressesCount: 15, // NOTE - this is the same as DailyNumberOfHolders - use `HoldingAddressesCount` instead for future updates
    CirculatingMarketValueDollar: 62,
    CirculatingAssetValueDollar: 63,
    MonthlyActiveAddresses: 43,
    BridgedTokenValueDollar: 71,
    BridgedTokenMarketCapDollar: 70,
    TotalContracts: 77,
    TotalCollateralizedFunds: 78,
    TotalIssuedFunds: 79,
    Apy7Day: 80,
    Apy30Day: 81,
    Trailing30DayTransferVolumeDollar: 1007,
    Trailing30DayTransferCount: 1006,
    Trailing30DayActiveAddressesCount: 58,

    // // // //
    // Bridge Token Measures
    DailyBridgeInflowVolume: 1008,
    DailyBridgeOutflowVolume: 1009,
    WeeklyBridgeInflowVolume: 1010,
    WeeklyBridgeOutflowVolume: 1011,
    MonthlyBridgeInflowVolume: 1012,
    MonthlyBridgeOutflowVolume: 1013,
    Trailing7DayBridgeInflowVolume: 1014,
    Trailing7DayBridgeOutflowVolume: 1015,
    Trailing30DayBridgeInflowVolume: 1016,
    Trailing30DayBridgeOutflowVolume: 1017,
    // New consolidated bridge volume measures
    DailyBridgeVolume: 1022,
    WeeklyBridgeVolume: 1023,
    MonthlyBridgeVolume: 1024,
    Trailing7DayBridgeVolume: 1025,
    Trailing30DayBridgeVolume: 1026,
    Hypothetical10KPerformance: 1027,
};

export const MEASURE_SLUGS = {
    adjusted_daily_burns_dollar: "adjusted_daily_burns_dollar",
    adjusted_daily_burns_token: "adjusted_daily_burns_token",
    adjusted_daily_mints_dollar: "adjusted_daily_mints_dollar",
    adjusted_daily_mints_token: "adjusted_daily_mints_token",
    adjusted_holding_addresses_count: "adjusted_holding_addresses_count",
    annually_active_addresses_count: "annually_active_addresses_count",
    apy_30_day: "apy_30_day",
    apy_7_day: "apy_7_day",
    asset_status: "asset_status",
    available_capacity_dollar: "available_capacity_dollar",
    available_capacity_token: "available_capacity_token",
    bridged_token_market_cap_dollar: "bridged_token_market_cap_dollar",
    bridged_token_supply_token: "bridged_token_supply_token",
    bridged_token_value_dollar: "bridged_token_value_dollar",
    cash_drag_percent: "cash_drag_percent",
    circulating_asset_value_dollar: "circulating_asset_value_dollar",
    circulating_market_value_dollar: "circulating_market_value_dollar",
    circulating_supply_token: "circulating_supply_token",
    cumulative_interest_earned_dollar: "cumulative_interest_earned_dollar",
    cumulative_interest_earned_token: "cumulative_interest_earned_token",
    cumulative_issued_amount_dollar: "cumulative_issued_amount_dollar",
    cumulative_issued_amount_token: "cumulative_issued_amount_token",
    cumulative_repaid_principal_dollar: "cumulative_repaid_principal_dollar",
    cumulative_repaid_principal_token: "cumulative_repaid_principal_token",
    cumulative_repo_collateral_volume: "cumulative_repo_collateral_volume",
    cumulative_repo_issuance_volume: "cumulative_repo_issuance_volume",
    cumulative_repo_trade_count: "cumulative_repo_trade_count",
    daily_active_addresses_count: "daily_active_addresses_count",
    daily_bridge_inflow_volume: "daily_bridge_inflow_volume",
    daily_bridge_outflow_volume: "daily_bridge_outflow_volume",
    daily_bridge_volume: "daily_bridge_volume",
    daily_burns_dollar: "daily_burns_dollar",
    daily_burns_token: "daily_burns_token",
    daily_mints_dollar: "daily_mints_dollar",
    daily_mints_token: "daily_mints_token",
    daily_transfer_volume_dollar: "daily_transfer_volume_dollar",
    daily_transfer_volume_token: "daily_transfer_volume_token",
    daily_yield_distributed_dollar: "daily_yield_distributed_dollar",
    daily_yield_distributed_token: "daily_yield_distributed_token",
    daily_yield_rate: "daily_yield_rate",
    defaulted_loans_amount_dollar: "defaulted_loans_amount_dollar",
    defaulted_loans_amount_percent: "defaulted_loans_amount_percent",
    drawndown_assets_dollar: "drawndown_assets_dollar",
    drawndown_assets_token: "drawndown_assets_token",
    funded_assets_dollar: "funded_assets_dollar",
    funded_assets_token: "funded_assets_token",
    holding_addresses_count: "holding_addresses_count",
    hypothetical_10_000_performance: "hypothetical_10_000_performance",
    idle_capital_dollar: "idle_capital_dollar",
    idle_capital_token: "idle_capital_token",
    interest_paid_dollar: "interest_paid_dollar",
    interest_paid_token: "interest_paid_token",
    investment_status: "investment_status",
    m2_money_stock: "m2_money_stock",
    market_value_dollar: "market_value_dollar",
    maximum_capacity_dollar: "maximum_capacity_dollar",
    monthly_active_addresses_count: "monthly_active_addresses_count",
    monthly_bridge_inflow_volume: "monthly_bridge_inflow_volume",
    monthly_bridge_outflow_volume: "monthly_bridge_outflow_volume",
    monthly_bridge_volume: "monthly_bridge_volume",
    monthly_transfer_count: "monthly_transfer_count",
    monthly_transfer_volume: "monthly_transfer_volume",
    net_asset_value_dollar: "net_asset_value_dollar",
    net_asset_value_token: "net_asset_value_token",
    number_of_active_loans: "number_of_active_loans",
    number_of_daily_transactions_count: "number_of_daily_transactions_count",
    number_of_defaulted_loans: "number_of_defaulted_loans",
    number_of_loans_funded: "number_of_loans_funded",
    outstanding_capital_dollar: "outstanding_capital_dollar",
    outstanding_principal_dollar: "outstanding_principal_dollar",
    outstanding_principal_token: "outstanding_principal_token",
    price_dollar: "price_dollar",
    principal_paid_dollar: "principal_paid_dollar",
    principal_paid_token: "principal_paid_token",
    quarterly_active_addresses_count: "quarterly_active_addresses_count",
    raw_supply: "raw_supply",
    rebase_multiplier: "rebase_multiplier",
    recovered_assets_dollar: "recovered_assets_dollar",
    recovered_assets_token: "recovered_assets_token",
    repo_loan_to_value_ratio: "repo_loan_to_value_ratio",
    secured_overnight_financing_rate: "secured_overnight_financing_rate",
    status: "status",
    top_10_holder_concentration: "top_10_holder_concentration",
    top_5_holder_concentration: "top_5_holder_concentration",
    top_holder_concentration: "top_holder_concentration",
    total_asset_value_dollar: "total_asset_value_dollar",
    total_asset_value_token: "total_asset_value_token",
    total_collateralized_funds: "total_collateralized_funds",
    total_contracts: "total_contracts",
    total_issued_funds: "total_issued_funds",
    total_supply_token: "total_supply_token",
    total_token_inflow: "total_token_inflow",
    total_token_outflow: "total_token_outflow",
    trailing_30_day_active_addresses_count:
        "trailing_30_day_active_addresses_count",
    trailing_30_day_bridge_inflow_volume:
        "trailing_30_day_bridge_inflow_volume",
    trailing_30_day_bridge_outflow_volume:
        "trailing_30_day_bridge_outflow_volume",
    trailing_30_day_bridge_volume: "trailing_30_day_bridge_volume",
    trailing_30_day_transfer_count: "trailing_30_day_transfer_count",
    trailing_30_day_transfer_volume: "trailing_30_day_transfer_volume",
    trailing_365_day_active_addresses_count:
        "trailing_365_day_active_addresses_count",
    trailing_7_day_active_addresses_count:
        "trailing_7_day_active_addresses_count",
    trailing_7_day_bridge_inflow_volume: "trailing_7_day_bridge_inflow_volume",
    trailing_7_day_bridge_outflow_volume:
        "trailing_7_day_bridge_outflow_volume",
    trailing_7_day_bridge_volume: "trailing_7_day_bridge_volume",
    trailing_7_day_transfer_count: "trailing_7_day_transfer_count",
    trailing_7_day_transfer_volume: "trailing_7_day_transfer_volume",
    trailing_90_day_active_addresses_count:
        "trailing_90_day_active_addresses_count",
    weekly_active_addresses_count: "weekly_active_addresses_count",
    weekly_bridge_inflow_volume: "weekly_bridge_inflow_volume",
    weekly_bridge_outflow_volume: "weekly_bridge_outflow_volume",
    weekly_bridge_volume: "weekly_bridge_volume",
    weekly_transfer_count: "weekly_transfer_count",
    weekly_transfer_volume: "weekly_transfer_volume",
    weighted_average_maturity: "weighted_average_maturity",
    written_off_principal_dollar: "written_off_principal_dollar",
    written_off_principal_token: "written_off_principal_token",
    yield_to_maturity: "yield_to_maturity",
};

export const BOOK_ENTRY_NETWORK_NAME = "book_entry";
export const BOOK_ENTRY_NETWORK_LABEL = "Off-Chain";
export const BOOK_ENTRY_NETWORK_COLOR_HEX = "#000000";

export const PROTOCOL_IDS = {
    Securitize: 10,
};

export const CANTON_NETWORK_NAME = "Canton";
export const BROADRIDGE_ASSET_TICKER = "BROADRIDGE-DLR-ASSET";
export const BROADRIDGE_PLATFORM_NAME = "Broadridge DLR";
export const REPURCHASE_AGREEMENTS_ASSET_CLASS_NAME = "Repurchase Agreements";

/**
 * @deprecated - use TOKENIZATION_TYPE_IDS instead
 */
export const TOKENIZATION_TYPE_NAMES = {
    ON_CHAIN_NATIVE: "Onchain Native",
    ON_CHAIN_REPRESENTED: "Onchain Represented",
    ON_CHAIN_DISTRIBUTED: "Onchain Distributed",
};

export const TOKENIZATION_TYPE_IDS = {
    DigitalTwin: 1,
    OnChainRepresented: 2,
    OnChainDistributed: 3,
};

export const DISTRIBUTION_TYPE_TOOLTIPS: Record<DistributionType, string> = {
    distributed:
        "RWA tokens using the blockchain as a distribution layer, enabling onchain investors to subscribe, hold, and manage assets directly through their own wallets or custodians.",
    represented:
        "RWA tokens using the blockchain as a recordkeeping layer, enhancing transparency, reconciliation, and operational efficiency without enabling onchain investor transfer or distribution.",
    all: "All RWA tokens, distributed and represented.",
};

export const DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO = {
    label: "Read the framework blog post",
    href: RWA_URLS.taxonomyDocs,
    analyticsLabel: TokenizationTypeUserEvents.ClickTokenizationTypeBadge,
};

// NOTE - the values in this array should be kept in lockstep with the asset.type column values in `rwa-api`
// Query to get distinct `asset.type` values:
// ```sql
// select DISTINCT(type) from dbt.fnl_dim_asset;
// ```
export const ASSET_TYPES = {
    Pool: "Pool",
    Loan: "Loan",
    ETF: "ETF",
    ETP: "ETP",
    MutualFund: "MUTUAL_FUND",
    Equity: "EQUITY",
} as const;

// NOTE - keep in lockstep with the `transferability_types` column values in `rwa-api`
export const TRANSFERABILITY_TYPES = {
    NotTransferable: "Not transferable",
    WithoutRestrictions: "Transferable without restrictions",
    WithinWhitelist: "Transferable within whitelist",
    WithBlacklist: "Transferable, with blacklist",
} as const;

export const NETWORK_IDS = {
    Solana: 2,
};
